import { engagementTheme } from './core/engagement-theme';
import {SlBrandingGateComponent, preloadBrandImages} from '@screenleads/branding';
import { slText } from '@screenleads/i18n';
import { SlI18nModule } from '@screenleads/i18n';
import {CaptureLanguageService} from './core/i18n/capture-language.service';
import {AppUpdateService} from './core/services/app-update.service';
import {SlAlertComponent,SlButtonComponent} from 'sl-dev-components';
import {PhoneVerificationComponent} from './features/auth/phone-verification.component';
import {LoginComponent} from './features/auth/login/login.component';
import { LoginRequestService } from './core/services/login-request.service';
import {SlDialogComponent} from 'sl-dev-components';
import { SlInstallPromptComponent } from 'sl-dev-components';
import { SlBottomNavComponent,SlBottomNavItem } from 'sl-dev-components';
import { SlToastContainerComponent } from 'sl-dev-components';
﻿import { Component,ViewChild,ElementRef, OnDestroy, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription, timeout } from 'rxjs';
import { SlCompanyColorsService } from 'sl-dev-components';
import { AuthService, CouponService, PromotionService } from './core/services';
import { FlowContextStore } from './core/stores';
import { BrandingStateService } from './core/services/branding-state.service';
import { environment } from '../environments/environment';

type StudioPreviewMessage = {
  type: 'SL_BRANDING_PREVIEW';
  payload?: {
    companyId: number | null;
    companyName: string;
    palette: Record<string, string>;
    logos: {
      mainUrl: string | null;
      wideUrl: string | null;
      squareUrl: string | null;
      onDarkUrl: string | null;
    };
  };
};

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [SlBrandingGateComponent,SlAlertComponent,SlButtonComponent,PhoneVerificationComponent,LoginComponent,SlDialogComponent,SlInstallPromptComponent, SlBottomNavComponent,SlToastContainerComponent,CommonModule, RouterOutlet,SlI18nModule],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent {
  private static readonly DEFAULT_COMPANY_ID = 1;
  readonly i18n=inject(CaptureLanguageService);
  title = slText("ScreenLeads Engagement");
  readonly appUpdates=inject(AppUpdateService);
  http = inject(HttpClient);
  authService = inject(AuthService);
  couponService = inject(CouponService);
  promotionService = inject(PromotionService);
  flowContextStore = inject(FlowContextStore);
  router = inject(Router);
  readonly loginRequest = inject(LoginRequestService);
  brandLogoUrl = signal('/logo-hor.svg');
  brandName = signal('ScreenLeads');
  brandStyles = signal<Record<string, string>>({});

  get bottomNavItems():SlBottomNavItem[]{return [{id:'promotions',label:this.i18n.copy().home,icon:'home'},{id:'my-coupons',label:this.i18n.copy().coupons,icon:'confirmation_number'},{id:'saved-promotions',label:this.i18n.copy().saved,icon:'bookmark'},{id:'redeem/scan',label:this.i18n.copy().scan,icon:'qr_code_scanner'},{id:'account',label:this.isAuthenticated()?this.i18n.copy().profile:this.i18n.copy().login,icon:'person'}];}
  get activeBottomNavId(){return this.isProfileNavActive()?'account':this.isActiveRoute('saved-promotions')?'saved-promotions':(this.isActiveRoute('my-coupons')||this.isActiveRoute('my-tickets')||this.isActiveRoute('coupons/'))?'my-coupons':this.isActiveRoute('redeem/scan')?'redeem/scan':'promotions';}
  navigateBottom(id:string){if(id==='account')this.navigateToProfile();else this.navigate(id);}
  // PWA Install Banner
  showInstallBanner = signal(false);
  bannerOffset=signal(0);
  private bannerObserver:ResizeObserver|null=null;
  @ViewChild('installBanner', {read: ElementRef}) set installBanner(element:ElementRef<HTMLElement>|undefined){
    this.bannerObserver?.disconnect();this.bannerObserver=null;
    if(!element){this.bannerOffset.set(0);return;}
    this.bannerObserver=new ResizeObserver(()=>this.bannerOffset.set(Math.ceil(element.nativeElement.getBoundingClientRect().height)+24));
    this.bannerObserver.observe(element.nativeElement);
  }
  isIosPwa = signal(false);
  private deferredPrompt: any = null;
  readonly brandingState = inject(BrandingStateService);
  private readonly companyColors = inject(SlCompanyColorsService);
  private routerSub?: Subscription;
  private brandingRequestKey: string | null = null;
  private brandCommit=0;
  retryBranding():void {this.brandingRequestKey=null;this.resolveBrandingFromUrl(this.router.url);}
  private readonly studioPreviewHandler = (event: MessageEvent) => {
    const message = event.data as StudioPreviewMessage;
    if (message?.type !== 'SL_BRANDING_PREVIEW' || !message.payload) {
      return;
    }

    this.applyStudioPreview(message.payload);
  };

  constructor() {
    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const nav = event as NavigationEnd;
        console.log('[Router] NavigationEnd:', {
          url: nav.url,
          urlAfterRedirects: nav.urlAfterRedirects,
          isAuthenticated: this.authService.isAuthenticated()
        });
        this.resolveBrandingFromUrl(nav.urlAfterRedirects || nav.url);
      });

    this.resolveBrandingFromUrl(this.router.url === '/' && typeof location !== 'undefined' ? location.pathname+location.search : this.router.url);

    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.studioPreviewHandler);

      // PWA: detect if already running in standalone mode
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
        || (window.navigator as any).standalone === true;

      if (!isStandalone) {
        // Respect user's previous dismissal (session-scoped)
        const dismissed = sessionStorage.getItem('pwa-banner-dismissed');
        if (dismissed) return;

        // Android/Desktop: intercept native install prompt
        window.addEventListener('beforeinstallprompt', (e: Event) => {
          e.preventDefault();
          this.deferredPrompt = e;
          this.showInstallBanner.set(true);
        });

        // iOS Safari: detect and show manual instructions after 3s
        const ua = window.navigator.userAgent;
        const isIos = /iphone|ipad|ipod/i.test(ua)
          || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
        const isSafari = /safari/i.test(ua) && !/chrome|crios|fxios|opios/i.test(ua);
        if (isIos && isSafari) {
          this.isIosPwa.set(true);
          window.setTimeout(() => this.showInstallBanner.set(true), 3000);
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.bannerObserver?.disconnect();
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.studioPreviewHandler);
    }
    this.routerSub?.unsubscribe();
  }

  isAuthenticated() {
    return this.authService.isAuthenticated();
  }

  isPublicRoute() {
    const url = this.router.url;
    return url.includes('/unsubscribe') || url.includes('/login') ||
      url.includes('/register') ||
      url.includes('/flow-error') ||
      url.includes('/promotion/');
  }

  installBannerVisible(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return this.showInstallBanner() && path !== '/promotions' && !/^\/promotion\/[^/]+$/.test(path) && !this.loginRequest.pending();
  }

  shouldShowBottomNav(): boolean {
    const url = this.router.url || '';
    return !url.includes('/flow-error') && !this.isStandalonePromotionRoute(url);
  }

  navigate(path: string): void {
    if (!this.isAuthenticated() && ['my-coupons','saved-promotions','redeem/scan','profile'].includes(path)) {
      this.loginRequest.request(`/${path}`, {companyId:this.flowContextStore.companyId() || AppComponent.DEFAULT_COMPANY_ID});
      return;
    }
    this.router.navigate([`/${path}`]);
  }

  navigateToProfile(): void {
    if(this.authService.isAuthenticated()) this.router.navigate(['/profile']);
    else this.loginRequest.request('/profile',{companyId:this.flowContextStore.companyId() || AppComponent.DEFAULT_COMPANY_ID});
  }

  isActiveRoute(path: string): boolean {
    return this.router.url.startsWith(`/${path}`);
  }

  isProfileNavActive(): boolean {
    return this.isActiveRoute('profile') || this.isActiveRoute('login') || this.isActiveRoute('register');
  }

  private isStandalonePromotionRoute(url: string): boolean {
    return /^\/(?:promotion|saved)\/\d+(?:[?#]|$)/.test(url);
  }

  getBrandLogoUrl(): string {
    return this.brandLogoUrl();
  }

  getBrandAlt(): string {
    return this.brandName();
  }

  getBrandStyles(): Record<string, string> {
    return this.brandStyles();
  }

  private resolveBrandingFromUrl(url: string) {
    const parsedUrl = this.router.parseUrl(url || this.router.url || '');
    const queryParams = parsedUrl.queryParams || {};
    const storeState = this.flowContextStore.state();

    const routePromotionId = this.extractPromotionIdFromUrl(url);
    const queryPromotionId = this.toPositiveInt(queryParams['promotionId']);
    const queryCompanyId = this.toPositiveInt(queryParams['companyId']);
    const storeCompanyId = this.toPositiveInt(storeState.companyId);

    // Only resolve branding from a promotionId when it comes from the URL itself
    // (route param or explicit query param). Never fall back to storeState.promotionId
    // because it may be stale from a previous QR scan session.
    const promotionId = routePromotionId ?? queryPromotionId;

    if (promotionId) {
      this.loadBrandingByPromotionId(promotionId);
      return;
    }

    const couponCode = this.extractCouponCodeFromUrl(url);
    if (couponCode) {
      this.loadBrandingByCouponCode(couponCode);
      return;
    }

    const companyId = queryCompanyId ?? storeCompanyId ?? this.toPositiveInt(this.authService.getCustomer()?.company?.id) ?? AppComponent.DEFAULT_COMPANY_ID;
    this.loadBrandingByCompanyId(companyId);
  }

  private loadBrandingByCompanyId(companyId: number) {
    const requestKey = `company:${companyId}`;
    if (this.brandingRequestKey === requestKey) {
      return;
    }

    this.brandingRequestKey = requestKey;
    this.brandingState.markPending();
    this.http.get<any>(`${environment.apiUrl}/companies/public/${companyId}/branding`).pipe(timeout(15000)).subscribe({
      next: (company) => {
        if (this.brandingRequestKey !== requestKey) return;
        if (!company) {
          this.setDefaultBranding();
          return;
        }

        this.flowContextStore.setContext({ companyId });
        this.applyCompanyBranding(company);
      },
      error: () => {
        if (this.brandingRequestKey !== requestKey) return;
        this.setDefaultBranding();
      }
    });
  }

  private loadBrandingByPromotionId(promotionId: number) {
    const requestKey = `promotion:${promotionId}`;
    this.brandingRequestKey = requestKey;
    this.brandingState.markPending();

    this.promotionService.getPublicPromotion(promotionId).pipe(timeout(15000)).subscribe({
      next: (promotion: any) => {
        if (this.brandingRequestKey !== requestKey) return;
        const company = promotion?.company;
        if (!company) {
          this.setDefaultBranding();
          return;
        }

        this.flowContextStore.setContext({
          promotionId,
          companyId: this.toPositiveInt(company.id) || null
        });
        this.loadBrandingByCompanyId(this.toPositiveInt(company.id) || AppComponent.DEFAULT_COMPANY_ID);
      },
      error: () => {
        if (this.brandingRequestKey !== requestKey) return;
        this.setDefaultBranding();
      }
    });
  }

  private loadBrandingByCouponCode(couponCode: string) {
    const requestKey = `coupon:${couponCode}`;
    this.brandingRequestKey = requestKey;
    this.brandingState.markPending();

    this.couponService.validateCoupon(couponCode).pipe(timeout(15000)).subscribe({
      next: (response: any) => {
        if (this.brandingRequestKey !== requestKey) return;
        const promotionId = this.toPositiveInt(
          response?.promotionId || response?.coupon?.promotionId || response?.coupon?.promotion?.id
        );
        if (!promotionId) {
          this.setDefaultBranding();
          return;
        }
        this.loadBrandingByPromotionId(promotionId);
      },
      error: () => {
        if (this.brandingRequestKey !== requestKey) return;
        this.setDefaultBranding();
      }
    });
  }

  private async applyCompanyBranding(company:any):Promise<void> {
    const key=this.brandingRequestKey,commit=++this.brandCommit;
    if(!company||Array.isArray(company)||!company.name){this.brandingState.markFailed();return;}
    const logo=this.resolveMediaUrl(company?.logoOnDark?.src||company?.logoOnDarkUrl||company?.logoWide?.src||company?.logoWideUrl||company?.logoHorizontalUrl||company?.logo?.src||company?.logoUrl)||'/logo-hor.svg';
    await preloadBrandImages([logo]);
    if(key!==this.brandingRequestKey||commit!==this.brandCommit)return;
    this.brandLogoUrl.set(logo);this.brandName.set(company.name);
    this.brandStyles.set({});
    this.companyColors.setCompany(engagementTheme({...company,logoWideUrl:logo}));
    this.brandingState.markReady();
  }
  private setDefaultBranding():void {
    // A failed tenant request must never reveal ScreenLeads as a substitute.
    this.brandingState.markFailed();
  }
  private applyStudioPreview(payload:NonNullable<StudioPreviewMessage['payload']>){
    this.brandingRequestKey='preview:'+ ++this.brandCommit;
    this.brandingState.markPending();
    this.applyCompanyBranding({...payload.palette,id:payload.companyId,name:payload.companyName,
      logoWideUrl:payload.logos?.wideUrl||payload.logos?.mainUrl,logoOnDarkUrl:payload.logos?.onDarkUrl});
  }

  private extractPromotionIdFromUrl(url: string): number | null {
    const match = (url || '').match(/\/(?:promotion|events)\/(\d+)/);
    return this.toPositiveInt(match?.[1]);
  }

  private extractCouponCodeFromUrl(url: string): string | null {
    const match = (url || '').match(/\/coupons\/code\/([^/?#]+)/);
    if (!match?.[1]) return null;
    return decodeURIComponent(match[1]);
  }

  private toPositiveInt(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      const parsed = Number(value);
      return parsed > 0 ? parsed : null;
    }
    return null;
  }

  private resolveMediaUrl(url:string|null|undefined):string|null {
    if(!url)return null;
    if(url.startsWith('http')||url.startsWith('/'))return url;
    return `/assets/${url}`;
  }
  async installPwa() {
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        this.showInstallBanner.set(false);
      }
      this.deferredPrompt = null;
    }
  }

  dismissBanner() {
    this.showInstallBanner.set(false);
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('pwa-banner-dismissed', '1');
    }
  }
}
